import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Network, Save, AlertTriangle, Check, Plus, Trash2, Eye, Lock, Bell, MessageCircle, Globe, Bot, Building2 } from 'lucide-react';
import { Button, Modal, ErrorDisplay } from '../components/common';
import { showToast } from '../components/common/Toast';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/layout/Header';
import { useAuthStore } from '../stores/authStore';
import { useOrganizationStore } from '../stores/organizationStore';
import { api } from '../services/api';

type CheckInMode = 'STATIC_LOCATION' | 'LIVE_LOCATION' | 'MINI_WEB';
type SettingsTab = 'organization' | 'checkin' | 'notifications';

interface OfficeLocation {
  latitude: number;
  longitude: number;
  radiusMeters: number;
  name?: string;
}

interface CheckInSettings {
  mode: CheckInMode;
  office: OfficeLocation | null;
  allowedNetworks: string[];
  kioskEnabled?: boolean;
  kioskDeviceCount?: number;
  kioskSubdomain?: string;
  kioskActivationCode?: string;
  autoCheckoutTime?: string;
  updatedAt?: string;
}

interface SystemSettings {
  id: string;
  restrictedVisibility: boolean;
  updatedAt: string;
  updatedById: string;
}

interface ReminderSettings {
  allowRemindersFromOthers: boolean;
  reminderChannels: {
    web: boolean;
    push: boolean;
    telegram: boolean;
  };
  reminderSound: boolean;
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { activeOrganizationId, organizations } = useOrganizationStore();
  const activeOrg = organizations.find(o => o.id === activeOrganizationId);
  const activeOrgName = activeOrg?.name || 'organization';
  const slugifiedName = activeOrgName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  const isAdmin = user?.organizationRoles?.find(r => r.orgId === activeOrganizationId)?.role === 'ADMIN';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<CheckInSettings>({
    mode: 'LIVE_LOCATION',
    office: null,
    allowedNetworks: [],
    kioskEnabled: false,
    kioskDeviceCount: 0,
    kioskSubdomain: '',
    kioskActivationCode: '',
    autoCheckoutTime: '',
  });
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
  const [reminderSettings, setReminderSettings] = useState<ReminderSettings>({
    allowRemindersFromOthers: true,
    reminderChannels: {
      web: true,
      push: false,
      telegram: true,
    },
    reminderSound: true,
  });
  const [newNetwork, setNewNetwork] = useState('');
  const [aiPreprompt, setAiPreprompt] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Tab navigation state
  const [activeTab, setActiveTab] = useState<SettingsTab>(isAdmin ? 'organization' : 'notifications');

  // Tab definitions
  const tabs: { id: SettingsTab; label: string; icon: React.ReactNode; adminOnly: boolean; description: string }[] = [
    { id: 'organization', label: t('settings.tabs.organization'), icon: <Building2 className="w-4 h-4" />, adminOnly: true, description: t('settings.tabs.organizationDescription') },
    { id: 'checkin', label: t('settings.tabs.checkinAttendance'), icon: <MapPin className="w-4 h-4" />, adminOnly: true, description: t('settings.tabs.checkinDescription') },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: <Bell className="w-4 h-4" />, adminOnly: false, description: t('settings.tabs.notificationsDescription') },
  ];

  const visibleTabs = isAdmin ? tabs : tabs.filter(tab => !tab.adminOnly);

  // Auto-switch to available tab for non-admins
  useEffect(() => {
    if (!isAdmin && activeTab !== 'notifications') {
      setActiveTab('notifications');
    }
  }, [isAdmin]);

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    setMessage(null);
  };

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // Clear settings to initial state before loading org-specific data
    setSettings({ mode: 'LIVE_LOCATION', office: null, allowedNetworks: [] });
    setSystemSettings(null);
    setReminderSettings({ allowRemindersFromOthers: true, reminderChannels: { web: true, push: false, telegram: true }, reminderSound: true });
    try {
      // Load check-in settings (available to all authenticated users)
      const checkInResponse = await api.get('/settings/checkin');
      if (checkInResponse.data.success) {
        setSettings(checkInResponse.data.data);
      }

      // Load reminder settings
      try {
        const reminderResponse = await api.get('/users/profile');
        if (reminderResponse.data.success && reminderResponse.data.data?.settings?.reminders) {
          setReminderSettings(reminderResponse.data.data.settings.reminders);
        }
      } catch (error) {
        console.error('Error loading reminder settings:', error);
        showToast(t('settings.error.loadReminder'), 'error');
      }

      // Load system settings (visibility) - only for admins
      // REV-3 fix: This is correctly gated by admin check - no redundant calls
      if (isAdmin) {
        try {
          const systemResponse = await api.get('/settings/system');
          if (systemResponse.data.success) {
            setSystemSettings(systemResponse.data.data);
          }
        } catch (error) {
          console.error('Error loading system settings:', error);
          showToast(t('settings.error.loadVisibility'), 'error');
        }

        try {
          const prePromptResponse = await api.get('/settings/ai-preprompt');
          if (prePromptResponse.data.success) {
            setAiPreprompt(prePromptResponse.data.data.aiPreprompt || '');
          }
        } catch (error) {
          console.error('Error loading AI preprompt:', error);
          showToast(t('settings.error.loadAiPreprompt'), 'error');
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoadError(t('settings.error.failedToLoadSome'));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadSettings();
  }, [user, activeOrganizationId, loadSettings]); // Reload on user or org change

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await api.put('/settings/checkin', {
        mode: settings.mode,
        officeLatitude: settings.office?.latitude,
        officeLongitude: settings.office?.longitude,
        officeRadiusMeters: settings.office?.radiusMeters,
        officeName: settings.office?.name,
        allowedNetworks: settings.allowedNetworks,
        kioskEnabled: settings.kioskEnabled,
        autoCheckoutTime: settings.autoCheckoutTime || null,
      });
      
      if (response.data.success) {
        setSettings(response.data.data);
        showToast(t('settings.success.checkinSaved'), 'success');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error?.message || t('settings.error.save') });
    } finally {
      setSaving(false);
    }
  };

  const saveSystemSettings = async () => {
    if (!systemSettings) return;
    
    setSaving(true);
    setMessage(null);
    try {
      const response = await api.put('/settings/system', {
        restrictedVisibility: systemSettings.restrictedVisibility,
      });
      
      if (response.data.success) {
        setSystemSettings(response.data.data);
        showToast(t('settings.success.visibilitySaved'), 'success');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error?.message || t('settings.error.saveVisibility') });
    } finally {
      setSaving(false);
    }
  };

  const saveAiPreprompt = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await api.put('/settings/ai-preprompt', { aiPreprompt });
      if (response.data.success) {
        setAiPreprompt(response.data.data.aiPreprompt || '');
        showToast(t('settings.success.aiPrepromptSaved'), 'success');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error?.message || t('settings.error.saveAiPreprompt') });
    } finally {
      setSaving(false);
    }
  };

  const saveReminderSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await api.put('/users/profile', {
        settings: {
          reminders: reminderSettings,
        },
      });
      
      if (response.data.success) {
        showToast(t('settings.success.reminderSaved'), 'success');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error?.message || t('settings.error.saveReminder') });
    } finally {
      setSaving(false);
    }
  };

  const addNetwork = async () => {
    if (!newNetwork) return;
    
    // Validate IP/CIDR format
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipRegex.test(newNetwork)) {
      setMessage({ type: 'error', text: t('settings.validation.invalidIpOrCidrFormat') });
      return;
    }

    if (settings.allowedNetworks.includes(newNetwork)) {
      setMessage({ type: 'error', text: t('settings.ui.networkAlreadyExists') });
      return;
    }

    try {
      const response = await api.post('/settings/networks', { network: newNetwork });
      if (response.data.success) {
        setSettings(prev => ({
          ...prev,
          allowedNetworks: response.data.data.networks,
        }));
        setNewNetwork('');
        showToast(t('settings.success.networkAdded'), 'success');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error?.message || t('settings.error.addNetwork') });
    }
  };

  const removeNetwork = async (network: string) => {
    try {
      const response = await api.delete(`/settings/networks/${encodeURIComponent(network)}`);
      if (response.data.success) {
        setSettings(prev => ({
          ...prev,
          allowedNetworks: response.data.data.networks,
        }));
        showToast(t('settings.success.networkRemoved'), 'success');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error?.message || t('settings.error.removeNetwork') });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <Header title={t('settings.title')} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
        </main>
      </div>
    );
  }

  const currentTabInfo = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="flex flex-col h-full">
      <Header title={t('settings.title')} />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {loadError && (
            <div className="mb-4">
              <ErrorDisplay message={loadError} variant="warning" dismissible onDismiss={() => setLoadError(null)} />
              <Button variant="secondary" onClick={() => { setLoadError(null); loadSettings(); }} className="mt-2">{t('settings.retry')}</Button>
            </div>
          )}
          {/* Tab Navigation - show only if more than one visible tab */}
          {visibleTabs.length > 1 && (
            <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto" role="tablist" aria-label={t('settings.ariaSectionsLabel')}>
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`tabpanel-${tab.id}`}
                  id={`tab-${tab.id}`}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Section description */}
          {currentTabInfo && (
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {currentTabInfo.description}
            </p>
          )}

          {/* Tab content panels */}
          <div
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >

          {/* Message banner - errors only; success feedback uses showToast */}
          {message && message.type === 'error' && (
            <div className="mb-6 p-4 rounded-lg flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              {message.text}
            </div>
          )}

          {/* ===================== ORGANIZATION SETTINGS TAB ===================== */}
          {activeTab === 'organization' && isAdmin && (
            <div className="space-y-8">
              {/* Task Visibility Section */}
              {systemSettings && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-5 h-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.taskVisibility')}</h2>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                    {t('settings.taskVisibilityDescription')}
                  </p>

                  <div className="space-y-4">
                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all active:scale-[0.98] ${
                      !systemSettings.restrictedVisibility 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}>
                      <input
                        type="radio"
                        name="visibilityMode"
                        checked={!systemSettings.restrictedVisibility}
                        onChange={() => setSystemSettings(prev => prev ? { ...prev, restrictedVisibility: false } : null)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          {t('settings.openVisibility')}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {t('settings.openVisibilityDescription')}
                        </div>
                      </div>
                    </label>

                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all active:scale-[0.98] ${
                      systemSettings.restrictedVisibility 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}>
                      <input
                        type="radio"
                        name="visibilityMode"
                        checked={systemSettings.restrictedVisibility}
                        onChange={() => setSystemSettings(prev => prev ? { ...prev, restrictedVisibility: true } : null)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          {t('settings.restrictedVisibility')}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {t('settings.restrictedVisibilityDescription')}
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Info box about private tasks */}
                  <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                      <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800 dark:text-amber-200">{t('settings.aboutPrivateTasks')}</h4>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          {t('settings.aboutPrivateTasksDescription')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <Button 
                      variant="primary" 
                      onClick={saveSystemSettings} 
                      disabled={saving}
                      leftIcon={saving ? undefined : <Save className="w-4 h-4" />}
                    >
                      {saving ? t('common.loading') : t('settings.ui.saveVisibility')}
                    </Button>
                  </div>
                </div>
              )}

              {/* AI Assistant Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Bot className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.aiAssistant')}</h2>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                  {t('settings.aiAssistantDescription')}
                </p>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="ai-preprompt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('settings.preprompt')}
                    </label>
                    <textarea
                      id="ai-preprompt"
                      rows={6}
                      maxLength={5000}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                      placeholder={t('settings.prepromptPlaceholder')}
                      value={aiPreprompt}
                      onChange={(e) => setAiPreprompt(e.target.value)}
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('settings.prepromptHint')}
                      </span>
                      <span className={`text-xs ${aiPreprompt.length > 4800 ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {aiPreprompt.length}/5000
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    variant="primary"
                    onClick={saveAiPreprompt}
                    disabled={saving}
                    leftIcon={saving ? undefined : <Save className="w-4 h-4" />}
                  >
                    {saving ? t('common.loading') : t('settings.ui.saveAiPreprompt')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ===================== CHECK-IN & ATTENDANCE TAB ===================== */}
          {activeTab === 'checkin' && isAdmin && (
            <div className="space-y-8">
              {/* Check-in Mode */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-5 h-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.checkinMode')}</h2>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                    {t('settings.checkinModeDescription')}
                  </p>

                  <div className="space-y-3">
                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all active:scale-[0.98] ${
                      settings.mode === 'STATIC_LOCATION' 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}>
                      <input
                        type="radio"
                        name="checkInMode"
                        value="STATIC_LOCATION"
                        checked={settings.mode === 'STATIC_LOCATION'}
                        onChange={() => setSettings(prev => ({ ...prev, mode: 'STATIC_LOCATION' }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{t('settings.modeStaticLocation')}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {t('settings.modeStaticLocationDescription')}
                        </div>
                      </div>
                    </label>

                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all active:scale-[0.98] ${
                      settings.mode === 'LIVE_LOCATION' 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}>
                      <input
                        type="radio"
                        name="checkInMode"
                        value="LIVE_LOCATION"
                        checked={settings.mode === 'LIVE_LOCATION'}
                        onChange={() => setSettings(prev => ({ ...prev, mode: 'LIVE_LOCATION' }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{t('settings.modeLiveLocation')}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {t('settings.modeLiveLocationDescription')}
                        </div>
                      </div>
                    </label>

                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all active:scale-[0.98] ${
                      settings.mode === 'MINI_WEB' 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}>
                      <input
                        type="radio"
                        name="checkInMode"
                        value="MINI_WEB"
                        checked={settings.mode === 'MINI_WEB'}
                        onChange={() => setSettings(prev => ({ ...prev, mode: 'MINI_WEB' }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{t('settings.modeMiniWeb')}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {t('settings.modeMiniWebDescription')}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* iPad Kiosk Setup directly inside Check-in Mode card */}
                <div className="border-t border-gray-100 dark:border-gray-750 pt-6">
                  <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/20 dark:to-purple-950/20 border border-indigo-100/70 dark:border-indigo-900 rounded-xl p-6 relative overflow-hidden">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('settings.ipadKioskTitle')}</h2>
                      <span className="text-xs font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/40 px-2 py-0.5 rounded-full">
                        {t('settings.skelloStyle')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      {t('settings.ipadKioskDescription')}
                    </p>

                    <div className="space-y-6">
                      {/* Enable Switch */}
                      <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800/80 rounded-lg border border-gray-100 dark:border-gray-700">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {t('settings.enableKiosk')}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('settings.enableKioskDescription')}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.kioskEnabled || false}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              kioskEnabled: e.target.checked,
                            }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-350 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>

                      {settings.kioskEnabled && (
                        <div className="bg-white/40 dark:bg-gray-800/40 backdrop-blur-sm rounded-lg border border-indigo-100/50 dark:border-indigo-900/50 p-4 space-y-4">
                          
                          {/* Subdomain URL Info */}
                          <div>
                            <span className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                              {t('settings.kioskSubdomainLabel')}
                            </span>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <div className="flex-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 font-mono text-sm text-gray-700 dark:text-gray-300 select-all truncate">
                                https://{settings.kioskSubdomain || `${slugifiedName}-xxxx`}.checkin.taskfly.io/
                              </div>
                              <Button
                                variant="secondary"
                                onClick={() => {
                                  const url = `https://${settings.kioskSubdomain || `${slugifiedName}-xxxx`}.checkin.taskfly.io/`;
                                  navigator.clipboard.writeText(url);
                                  showToast(t('settings.ui.kioskLinkCopied'), 'success');
                                }}
                                className="text-xs h-[38px] px-3 font-semibold"
                              >
                                {t('settings.copyLink')}
                              </Button>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              {t('settings.kioskSubdomainHint')}
                            </p>
                          </div>

                          {/* Activation Code — always visible */}
                          <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                              <div className="flex-1">
                                <span className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                  {t('settings.activationCode')}
                                </span>
                                <div className="text-2xl font-bold tracking-widest text-indigo-600 dark:text-indigo-400 font-mono">
                                  {settings.kioskActivationCode || '------'}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {t('settings.activationCodeDescription')}
                                </p>
                              </div>
                              <div className="flex items-start">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => setShowRegenerateConfirm(true)}
                                  className="text-xs h-[38px] px-3 font-semibold"
                                >
                                  🔄 {t('settings.regenerate')}
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Device count / status */}
                          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
                            {(settings.kioskDeviceCount ?? 0) > 0 ? (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                  <div>
                                    <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">
                                      {t('settings.devicesConnected', { count: settings.kioskDeviceCount ?? 0 })}
                                    </h4>
                                    <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                                      {t('settings.devicesActiveReady')}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant="secondary"
                                  onClick={() => setShowDisconnectConfirm(true)}
                                  className="text-xs border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                                >
                                  {t('settings.disconnectAll')}
                                </Button>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                <p>{t('settings.noDevicesConnected')}</p>
                              </div>
                            )}
                          </div>

                          {/* Auto-checkout time */}
                          <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                              <div className="flex-1">
                                <span className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                  {t('settings.autoCheckoutTime') || 'Auto-checkout time'}
                                </span>
                                <input
                                  type="time"
                                  value={settings.autoCheckoutTime || ''}
                                  onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    autoCheckoutTime: e.target.value,
                                  }))}
                                  className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {t('settings.autoCheckoutTimeDescription') || 'Time at which users who have not checked out will be automatically checked out. Time is in UTC.'}
                                </p>
                              </div>
                            </div>
                          </div>

                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Office Location */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.officeLocation')}</h2>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                  {t('settings.officeLocationDescription')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('settings.latitude')}
                    </label>
                    <input
                      type="number"
                      step="0.00000001"
                      value={settings.office?.latitude || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        office: {
                          ...prev.office,
                          latitude: parseFloat(e.target.value) || 0,
                          longitude: prev.office?.longitude || 0,
                          radiusMeters: prev.office?.radiusMeters || 100,
                        },
                      }))}
                      placeholder={t('settings.ui.placeholderLatitude')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('settings.longitude')}
                    </label>
                    <input
                      type="number"
                      step="0.00000001"
                      value={settings.office?.longitude || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        office: {
                          ...prev.office,
                          longitude: parseFloat(e.target.value) || 0,
                          latitude: prev.office?.latitude || 0,
                          radiusMeters: prev.office?.radiusMeters || 100,
                        },
                      }))}
                      placeholder={t('settings.ui.placeholderLongitude')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('settings.radiusMeters')}
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="10000"
                      value={settings.office?.radiusMeters || 100}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        office: {
                          ...prev.office,
                          radiusMeters: parseInt(e.target.value) || 100,
                          latitude: prev.office?.latitude || 0,
                          longitude: prev.office?.longitude || 0,
                        },
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('settings.locationName')}
                    </label>
                    <input
                      type="text"
                      value={settings.office?.name || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        office: {
                          ...prev.office,
                          name: e.target.value,
                          latitude: prev.office?.latitude || 0,
                          longitude: prev.office?.longitude || 0,
                          radiusMeters: prev.office?.radiusMeters || 100,
                        },
                      }))}
                      placeholder={t('settings.ui.placeholderOfficeName')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Allowed Networks */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Network className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.allowedNetworks')}</h2>
                  {settings.mode !== 'MINI_WEB' && (
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                      {t('settings.onlyAppliesMiniWeb')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                  {t('settings.allowedNetworksDescription')}
                </p>

                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <input
                     type="text"
                     value={newNetwork}
                     onChange={(e) => setNewNetwork(e.target.value)}
                     placeholder={t('settings.ui.placeholderNetwork')}
                     className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                     onKeyDown={(e) => e.key === 'Enter' && addNetwork()}
                  />
                  <Button variant="secondary" onClick={addNetwork} leftIcon={<Plus className="w-4 h-4" />} className="w-full sm:w-auto min-h-[44px]">
                    {t('settings.add')}
                  </Button>
                </div>

                {settings.allowedNetworks.length > 0 ? (
                  <div className="space-y-2">
                    {settings.allowedNetworks.map((network) => (
                      <div 
                        key={network}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{network}</span>
                        <button
                          onClick={() => removeNetwork(network)}
                          className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                    {t('settings.noNetworksConfigured')}
                  </div>
                )}
              </div>

              {/* Security Info */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-800 dark:text-amber-200">{t('settings.securityAlerts')}</h3>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      {t('settings.securityAlertsDescription')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Save all check-in settings */}
              <div className="flex justify-end">
                <Button 
                  variant="primary" 
                  onClick={saveSettings} 
                  disabled={saving}
                  leftIcon={saving ? undefined : <Save className="w-4 h-4" />}
                  className="w-full sm:w-auto min-h-[44px]"
                >
                  {saving ? t('common.loading') : t('settings.ui.saveCheckin')}
                </Button>
              </div>
            </div>
          )}

          {/* ===================== NOTIFICATIONS & REMINDERS TAB ===================== */}
          {activeTab === 'notifications' && (
            <div className="space-y-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Bell className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.reminderSettings')}</h2>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                  {t('settings.reminderSettingsDescription')}
                </p>

                <div className="space-y-6">
                  {/* Allow reminders from others */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {t('settings.allowRemindersFromOthers')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('settings.allowRemindersFromOthersDescription')}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reminderSettings.allowRemindersFromOthers}
                        onChange={(e) => setReminderSettings(prev => ({
                          ...prev,
                          allowRemindersFromOthers: e.target.checked,
                        }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-gray-200 dark:border-gray-700"></div>

                  {/* Notification channels */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                      {t('settings.notificationChannels')}
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={reminderSettings.reminderChannels.web}
                          onChange={(e) => setReminderSettings(prev => ({
                            ...prev,
                            reminderChannels: {
                              ...prev.reminderChannels,
                              web: e.target.checked,
                            },
                          }))}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <Globe className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('settings.webNotifications')}</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.webNotificationsDescription')}</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={reminderSettings.reminderChannels.telegram}
                          onChange={(e) => setReminderSettings(prev => ({
                            ...prev,
                            reminderChannels: {
                              ...prev.reminderChannels,
                              telegram: e.target.checked,
                            },
                          }))}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <MessageCircle className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('settings.telegramNotifications')}</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.telegramNotificationsDescription')}</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={reminderSettings.reminderChannels.push}
                          onChange={(e) => setReminderSettings(prev => ({
                            ...prev,
                            reminderChannels: {
                              ...prev.reminderChannels,
                              push: e.target.checked,
                            },
                          }))}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <Bell className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('settings.pushNotifications')}</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.pushNotificationsDescription')}</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-gray-200 dark:border-gray-700"></div>

                  {/* Sound */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {t('settings.reminderSound')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('settings.reminderSoundDescription')}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reminderSettings.reminderSound}
                        onChange={(e) => setReminderSettings(prev => ({
                          ...prev,
                          reminderSound: e.target.checked,
                        }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>

                {/* Save button for reminder settings */}
                <div className="flex justify-end mt-6">
                  <Button 
                    variant="primary" 
                    onClick={saveReminderSettings} 
                    disabled={saving}
                    leftIcon={saving ? undefined : <Save className="w-4 h-4" />}
                  >
                    {saving ? t('common.loading') : t('settings.ui.saveReminder')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </main>

      <Modal isOpen={showRegenerateConfirm} onClose={() => setShowRegenerateConfirm(false)} title={t('settings.ui.regenerateActivationCode')} size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.regenerateCodeDescription')}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowRegenerateConfirm(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={async () => {
            setShowRegenerateConfirm(false);
            setSaving(true);
            try {
              const response = await api.put('/settings/checkin', { regenerateKioskCode: true });
              if (response.data.success) {
                setSettings(prev => ({ ...prev, kioskActivationCode: response.data.data.kioskActivationCode, kioskDeviceCount: 0 }));
                setMessage({ type: 'success', text: t('settings.success.newActivationCodeGenerated') });
              }
            } catch (e) {
              console.error(e);
              showToast(t('settings.error.regenerateActivationCode'), 'error');
            } finally { setSaving(false); }
          }}>{t('settings.regenerateButton')}</Button>
        </div>
      </Modal>

      <Modal isOpen={showDisconnectConfirm} onClose={() => setShowDisconnectConfirm(false)} title={t('settings.ui.disconnectAllDevices')} size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.disconnectDevicesDescription')}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowDisconnectConfirm(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={async () => {
            setShowDisconnectConfirm(false);
            setSaving(true);
            try {
              const response = await api.delete('/kiosk/devices');
              if (response.data.success) {
                setSettings(prev => ({ ...prev, kioskDeviceCount: 0, kioskActivationCode: response.data.data.kioskActivationCode }));
                setMessage({ type: 'success', text: t('settings.success.allDevicesDisconnected') });
              }
            } catch (e) {
              console.error(e);
              showToast(t('settings.error.disconnectDevices'), 'error');
            } finally { setSaving(false); }
          }}>{t('settings.disconnectAll')}</Button>
        </div>
      </Modal>
    </div>
  );
};

export default SettingsPage;
